import { loadDevExtremeStyles } from "../devextreme/styles";

/**
 * Loads datagrid dependency async and wraps datagrid creation function.
 *
 * Why does this function take a function ?
 * This is current code using initalizating data grid rely on a lot of side effects.
 * These side effects should be progressively removed so that creation of the creation can be made here and not just wrapped.
 *
 * This function is a meant at a first step into a more generic datagrid usage in the app.
 *
 * Don't hesitate to improve it, for example by also supporting column choosing.
 *
 * @param {Function} datagridCreateFn
 * @param {Object} options
 * @returns {Object}
 */
export async function createDatagrid(datagridCreateFn, options = {}) {
  try {
    const [{ default: DataGrid }] = await Promise.all([import("devextreme/ui/data_grid"), loadDevExtremeStyles()]);

    const dataGrid = datagridCreateFn({ default: DataGrid });

    const dynamicHeight = options.dynamicHeight ?? true;

    if (dynamicHeight) {
      dynamicGridHeight(dataGrid);
    }

    const exportBtnEl = document.querySelector(".js-datagrid-export");

    if (exportBtnEl) {
      const exportName = options.exportName || exportBtnEl.dataset.exportName;
      exportBtnEl.addEventListener("click", () => exportGridToExcel(dataGrid, exportName, options.customizeCell));
    }

    return dataGrid;
  } catch (error) {
    console.error(error);
    throw `Impossible to load DataGrid : ${error}`;
  }
}

/**
 * Loads pivotgrid dependency async and wraps pivotgrid creation function.
 *
 * This function is the counterpart of `createDatagrid` and should be used in the same way,
 * improvement on one should be reflected on the other.
 *
 * @param {Function} datagridCreateFn
 * @param {Object} options
 * @returns {Object}
 */
export async function createPivotGrid(pivotGridCreateFn, options = {}) {
  try {
    const [{ default: PivotGrid }] = await Promise.all([import("devextreme/ui/pivot_grid"), loadDevExtremeStyles()]);

    const pivotGrid = pivotGridCreateFn({ default: PivotGrid });

    if (options.dynamicHeight ?? true) {
      dynamicGridHeight(pivotGrid);
    }

    const exportBtnEl = document.querySelector(".js-datagrid-export");
    if (exportBtnEl) {
      const exportName = options.exportName || exportBtnEl.dataset.exportName;
      exportBtnEl.addEventListener("click", () => {
        console.warn("exportGridToExcel called");
        exportGridToExcel(pivotGrid, exportName, options.customizeCell);
      });
    }

    return pivotGrid;
  } catch (error) {
    throw `Impossible to load PivotGrid : ${error}`;
  }
}

/**
 * Returns default options for datagrids.
 *
 * Implemented as a function in order to open the way to multiple modes of display with their defaults.
 * We have 2 or 3 ways to render our datagrids but options they are always hardcoded.
 * A parameter could be used to set the appropriate options.
 * The localstorage is not used when there are filter url params, because visibility of columns is already handled by the filters.
 *
 * @returns {Object}
 */
export const datagridDefaultOptions = (storageKey) => {
  const hasParams = hasQueryStringParams();
  const shouldUseStorage = !hasParams && !!storageKey;
  console.debug("Storage enabled:", shouldUseStorage);

  return {
    columnResizingMode: "widget",
    // RGAA 7.1 / WCAG 2.1.1 — DevExtreme disables keyboard navigation by default.
    keyboardNavigation: { enabled: true, enterKeyAction: "moveFocus" },
    // Performance improvements when there are lot of columns and rows
    scrolling: {
      rowRenderingMode: "virtual",
      columnRenderingMode: "virtual",
    },
    stateStoring: {
      enabled: shouldUseStorage,
      type: "custom",
      customLoad: function () {
        return JSON.parse(localStorage.getItem(storageKey));
      },
      customSave: function (state) {
        //When we enable stateStoring on a DevExtreme datagrid, it keeps all filters values in the local storage, including searchbar value
        //We don't want the value entered in the searchbar to be kept in the local storage
        //This custom save implement the save of all filters except the searchbar one
        state.searchText = "";
        localStorage.setItem(storageKey, JSON.stringify(state));
      },
    },
  };
};

export const pagination = (min = 100, def = 200, max = 500) => ({
  paging: {
    enabled: true,
    pageSize: def,
  },
  pager: {
    showPageSizeSelector: true,
    allowedPageSizes: [min, def, max],
    showInfo: true,
    showNavigationButtons: true,
  },
  scrolling: {
    mode: "standard",
  },
});

// Contains options to configure using the column chooser
export const optionsToolbar = {
  columnChooser: {
    enabled: true,
    mode: "select",
    search: {
      enabled: true,
    },
    height: 700,
    width: 400,
    selection: {
      selectByClick: true,
    },
  },
  onToolbarPreparing(e) {
    e.toolbarOptions.items = e.toolbarOptions.items.map((e) =>
      e.name == "columnChooserButton"
        ? {
            widget: "dxButton",
            name: "columnChooserButton",
            options: { visible: false },
          }
        : e
    );
  },
};

export function onSelectionChanged(dataGrid) {
  const nonEmpty = dataGrid.getSelectedRowKeys().map((x) => x.id).length > 0;
  if (document.querySelector(".batch-removal")) {
    document.querySelector(".batch-removal").classList.toggle("hidden", !nonEmpty);
  }
}

export function checkBatchButtonsAndPopupWarning(dataGrid) {
  const selectedRows = dataGrid.getSelectedRowKeys();
  const nonEmpty = selectedRows.length > 0;

  if (document.querySelector(".batch-removal")) {
    document.querySelector(".batch-removal").classList.toggle("hidden", !nonEmpty);
  }
  if (document.querySelector(".batch-anonymize-button")) {
    document.querySelector(".batch-anonymize-button").classList.toggle("hidden", !nonEmpty);
  }
  if (document.querySelector(".js-warning-block-trainees-in-sessions")) {
    /*
    If there is a selected trainee that is in a session whose end date is in the future,
    we add a warning to the batch anonymization popup.
    */
    const now = new Date();
    const currentDateWithoutTime = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const traineeInTrainingSession = selectedRows.some((row) => {
      return row.latest_training_session_date && Date.parse(row.latest_training_session_date) >= currentDateWithoutTime;
    });

    document.querySelector(".js-warning-block-trainees-in-sessions").classList.toggle("hidden", !traineeInTrainingSession);
  }
}

export function changeSearch(dataGrid, selector) {
  if (document.querySelector(selector)) {
    dataGrid.searchByText(document.querySelector(selector).value);
  }
}

/**
 * Loads necessary dependencies for excel export async and then exports the datagrid into an xlsx file.
 *
 * `customizeCell` argument could and should be replaced by being directly set in datagrid params
 * Unfortunely it's not doable due to the way we instanciate datagrids atm (03/09/24).
 *
 * @param {Object} instance
 * @param {String} exportName
 * @param {Function | undefined} customizeCell
 */
export async function exportGridToExcel(instance, exportName, customizeCell = undefined) {
  const [ExcelExporter, { Workbook }, { default: saveAs }] = await Promise.all([
    import("devextreme/excel_exporter"),
    import("exceljs"),
    import("file-saver"),
  ]);

  const cleanExportName = cleanWorksheetName(exportName);

  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet(cleanExportName);

  const exportOptions = {
    component: instance,
    worksheet: worksheet,
    autoFilterEnabled: true,
    customizeCell: customizeCell,
  };

  switch (instance.NAME) {
    case "dxDataGrid":
      await ExcelExporter.exportDataGrid(exportOptions);
      break;
    case "dxPivotGrid":
      await ExcelExporter.exportPivotGrid(exportOptions);
      break;
    default:
      throw new Error("Unknown component");
  }

  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${cleanExportName.toLowerCase()}.xlsx`);
}

// See https://github.com/exceljs/exceljs/blob/5bed18b45e824f409b08456b59b87430ded023ab/lib/doc/worksheet.js#L137
function cleanWorksheetName(name) {
  const invalidWorksheetMatch = /[\\/*[\]:?"']|^History$/g;

  const cleanName = name.trim().replace(invalidWorksheetMatch, "").substring(0, 31); // Excel max worsheet name length

  if (!cleanName) {
    return "Feuille1";
  }

  return cleanName;
}

/**
 * Sets grid height dynamically.
 *
 * Rules for rendering :
 *
 * 1. Datagrid should always take a minimum height of >570px ~= 10 lines of data (- totals - pagination - ...).
 * 2. Page scroll should be avoided when possible
 * 3. If page scroll is not avoidable then the page should be scrollable until we see only the datagrid
 *
 * @param {Object} grid
 */
export function dynamicGridHeight(grid) {
  const MINIMUM_DATAGRID_HEIGHT = 570; // px

  const el = grid._$element[0];
  const parentContainer = el.closest(".container-el__padded_body");
  const buttonGroup = el.previousElementSibling;

  const calcAndSetHeight = () => {
    const bottomNavHeight = document.querySelector(".app-nav-mobile")?.clientHeight || 0;
    const appNavHeight = document.querySelector(".app-page__navbar")?.clientHeight || 0;

    const isfinanceAnalysis = document.getElementById("entity-email-history-datagrid");
    const dialog = el.closest("dialog");

    if (dialog) {
      // If the datagrid is rendered inside a popup it should take the whole height without scroll page
      el.style.setProperty(
        "height",
        `calc(${dialog.clientHeight}px - ${buttonGroup.clientHeight}px - 2 * var(--popup-zoomed-content-margin) - 2 * var(--popup-inner-padding)`
      );
    } else if (el.offsetTop + MINIMUM_DATAGRID_HEIGHT >= document.body.clientHeight) {
      // If the datagrid has not (approx) enough space to be rendered its parent should be big enough so that we can have it in full height
      const buttonGroupHeight = buttonGroup ? buttonGroup.clientHeight : 0;
      parentContainer?.style.setProperty(
        "height",
        `calc(${
          isfinanceAnalysis
            ? `${document.body.clientHeight - bottomNavHeight - el.offsetTop}px - var(--container-el-padding)`
            : `${document.body.clientHeight - bottomNavHeight - appNavHeight}px - 2 * var(--app-page-content-y-padding)`
        })`
      );
      el.style.setProperty("height", `calc(100% - ${buttonGroupHeight}px)`);
    } else {
      // If the datagrid has enough space to be rendered without page scroll we give the remaining space on the page
      parentContainer?.style.removeProperty("height");
      el.style.setProperty(
        "height",
        `calc(${
          isfinanceAnalysis
            ? `${
                document.body.clientHeight - el.offsetTop - appNavHeight
              }px - var(--container-el-margin) - var(--app-page-content-y-padding)`
            : `${
                document.body.clientHeight - el.offsetTop - bottomNavHeight
              }px - var(--container-el-padding) - var(--container-el-margin) - var(--app-page-content-y-padding)`
        })`
      );
    }
  };

  calcAndSetHeight();

  const ro = new ResizeObserver(() => calcAndSetHeight());
  ro.observe(el.parentElement);
}

/**
 *
 * We want to have clear buttons on our columns filters to allow the user to know if the filter is filled or not
 * The property for clear buttons has to be add to all columns
 *
 * @param {string} storageKey
 * @param {Object} state (datagrid filters values)
 */
export function filtersClearButton(columns) {
  return columns.map((col) => {
    let subCols = col.columns || [];
    subCols = subCols.map((c) => {
      return { ...c, editorOptions: { showClearButton: true } };
    });

    return { ...col, editorOptions: { showClearButton: true }, columns: subCols };
  });
}

/**
 *
 * We want a generic filter removal button with filter amount on it.
 * The button and the badge should be added to the html before-hand.
 *
 * @param {Object} datagrid
 * @param {Object} [options = {}]
 * @param {HTMLElement} [options.$button]
 * @param {HTMLElement} [options.$badge]
 */
export function genericFilterSummary(dataGrid, options = {}) {
  const $button = options.$button ?? document.querySelector("#filter-amount-button");
  const $badge = options.$badge ?? document.querySelector("#filter-amount");

  const filtersAmount = getFiltersAmount(dataGrid);
  const display = filtersAmount == 0 ? "none" : "inline-flex";
  $badge.innerText = filtersAmount;
  $button.style.display = display;
}

function getFiltersAmount(dataGrid) {
  let filterCount = 0;
  if (dataGrid.option().searchPanel.text) filterCount++;
  for (let idx = 0; ; idx++) {
    const column = dataGrid.columnOption(idx);
    if (column === undefined) break;
    // would contain a value for the "=", "<", ... filters
    if (column.filterValue) filterCount++;
    // would contain an array for the included/excluded values filter
    if (column.filterValues?.length > 0) filterCount++;
  }
  return filterCount;
}

/**
 * Check if any of the given query string params exist
 * @param {string[]} params - Query string keys to check (defaults to ["filter", "column"])
 * @returns {Boolean}
 */
function hasQueryStringParams(params = ["filter", "column"]) {
  return params.some((param) => queryStringArray(param)?.length > 0);
}

/**
 * Returns the storage key if no query params exist, otherwise null.
 * Used for finance tabular urls generated by Petronille.
 * @param {string} storageKey - Storage key for the tabular
 * @returns {string|null}
 */
export function getStorageKey(storageKey) {
  console.debug("localStorageEnabled: ", !hasQueryStringParams());
  return hasQueryStringParams() ? null : storageKey;
}

/**
 * Extract and parse a JSON array from the URL query string.
 * Returns an empty array if param is missing or invalid.
 * Mostly used for Petronille for finance tabulars.
 * The filters must follow the complex filters schema supported by dataGrid
 * https://js.devexpress.com/jQuery/Documentation/21_1/ApiReference/Data_Layer/DataSource/Configuration/#filter
 *  [
 *   ["dataField", "operator", "value"],
 *   "and",
 *   [
 *     [
 *       [ "anotherDataField", "<", 3 ],
 *       "or",
 *       ["anotherDataField", ">", 11 ]
 *     ]
 *   ]
 *  ]
 * @param {string} key - Query string parameter name
 * @returns {Array}
 */
export function queryStringArray(key) {
  const value = new URLSearchParams(window.location.search).get(key);

  if (!value) return [];

  try {
    const parsed = JSON.parse(decodeURIComponent(value));
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error(`Invalid query param "${key}":`, error);
    return [];
  }
}

/**
 *  Apply filters to DataGrid
 *  Set all columns to visible = false, and set visible: true only to filtered colums, visibleColums defaultVisibleColumns
 * @param {Object} datagrid
 * @param {Array} defaultVisibleColumns
 */
export function applyQueryStringFilters(dataGrid, defaultVisibleColumns = []) {
  const filters = queryStringArray("filter");
  const visibleColums = queryStringArray("column");
  if (filters.length > 0 || visibleColums.length > 0) {
    try {
      dataGrid.beginUpdate();
      const { columns: columnsNameInFilter, filterValues } = extractFilterInfo(filters);
      // Hide all columns
      const allColumns = dataGrid.option("columns");
      const columnsToShow = new Set([...columnsNameInFilter, ...visibleColums, ...defaultVisibleColumns]);
      allColumns.forEach((column) => {
        if (column.caption) {
          dataGrid.columnOption(column.caption, "visible", false);
        }
        if (column.columns) {
          column.columns.map((col) => dataGrid.columnOption(col.dataField, "visible", false));
        }
      });

      // Show filtered columns
      columnsToShow.forEach((column) => {
        if (dataGrid.columnOption(column)) {
          dataGrid.columnOption(column, "visible", true);
          const columnType = dataGrid.columnOption(column, "dataType");
          // For filtered values, set value and operator in input cells header
          if (filterValues[column]?.value && filterValues[column]?.operation) {
            let value = filterValues[column].value;

            if (columnType === "date") {
              const date = new Date(value);
              value = isNaN(date) ? value : date;
            }

            dataGrid.columnOption(column, "filterValue", value);
            dataGrid.columnOption(column, "selectedFilterOperation", filterValues[column].operation);
            dataGrid.columnOption(column, "filterValues", value);
          }
        }
      });

      dataGrid.endUpdate();
      console.debug("Applied complex filter successfully");
      return;
    } catch (e) {
      console.error("Failed to apply complex filter:", e);
    }
  }
}

/**
 * Returns column names from filter array on column, and filterValues
 * @param {Array} filterArray
 * @returns {Object} columns: an array of columnNames, filterValues: an object with the value and the operation
 */
function extractFilterInfo(filterArray) {
  const columns = new Set();
  const filterValues = {};

  function traverse(arr) {
    if (!Array.isArray(arr)) return;

    for (let i = 0; i < arr.length; i++) {
      const item = arr[i];
      if (Array.isArray(item)) {
        // Check if this is a filter condition [fieldName, operator, value]
        if (item.length === 3 && typeof item[0] === "string" && typeof item[1] === "string") {
          const [fieldName, operation, value] = item;

          columns.add(fieldName);
          filterValues[fieldName] = { value, operation };
        } else {
          // Nested group, traverse it
          traverse(item);
        }
      }
    }
  }

  traverse(filterArray);

  return {
    columns: Array.from(columns),
    filterValues,
  };
}
